import { LocalDate } from "@js-joda/core";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { ListRow } from "components/designV2/listRow/listRow";
import { Modal } from "components/designV2/modal/modal";
import { RadioButton } from "components/designV2/radioButton/radioButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import type { CompanyWorker } from "model/Company";
import type {
  GenerateSalaryNoticesMode,
  GenerateSalaryNoticesModalState,
} from "./useGenerateSalaryNoticesModal";
import {
  GenerateSalaryNoticesModalContent,
  GenerateSalaryNoticesOptions,
  GenerateSalaryNoticesWorkerAction,
  GenerateSalaryNoticesWorkerCount,
  GenerateSalaryNoticesWorkerList,
} from "./salary.styled";

interface GenerateSalaryNoticesModalProps {
  avatarUrls: Record<string, string>;
  canCreateSalaryNotices: boolean;
  cutOffDate: LocalDate;
  generateSalaryNoticesMode: GenerateSalaryNoticesMode;
  isCutOffWarningOpen: boolean;
  isGeneratingSalaryNotices: boolean;
  isOpen: boolean;
  modalState: GenerateSalaryNoticesModalState;
  salaryNoticeWorkers: CompanyWorker[];
  selectedWorkerIds: number[];
  onCancelCutOffWarning: () => void;
  onClose: () => void;
  onConfirmCreate: () => Promise<void>;
  onCreate: () => Promise<void>;
  onModeChange: (value: string) => void;
  onToggleWorker: (workerId: number) => void;
  onWorkerActionClick: (event: MouseEvent<HTMLDivElement>) => void;
}

const getWorkerName = (worker: CompanyWorker): string => {
  const name = [worker.firstName, worker.lastName]
    .filter((part): part is string => Boolean(part))
    .join(" ")
    .trim();
  return name || `ID ${worker.id}`;
};

const formatCutOffDate = (date: LocalDate, language: string): string =>
  new Intl.DateTimeFormat(language.startsWith("sv") ? "sv-SE" : "en-GB", {
    day: "numeric",
    month: "long",
  }).format(new Date(date.year(), date.monthValue() - 1, date.dayOfMonth()));

const assertNever = (value: never): never => {
  throw new Error(
    `Unhandled generate salary notices modal state: ${JSON.stringify(value)}`,
  );
};

export const GenerateSalaryNoticesModal = (
  props: GenerateSalaryNoticesModalProps,
): JSX.Element => {
  const { i18n, t } = useTranslation();
  const cutOffDate = formatCutOffDate(props.cutOffDate, i18n.language);

  const renderFooter = (): JSX.Element => {
    if (props.isCutOffWarningOpen) {
      return (
        <>
          <SecondaryButton
            label={t("Salary.Cancel")}
            onClick={props.onCancelCutOffWarning}
            size="small"
            disabled={props.isGeneratingSalaryNotices}
          />
          <PrimaryButton
            label={t("Salary.GenerateSalaryNoticesCreateAnyway")}
            onClick={() => void props.onConfirmCreate()}
            size="small"
            isLoading={props.isGeneratingSalaryNotices}
          />
        </>
      );
    }

    return (
      <>
        <SecondaryButton
          label={t("Salary.Cancel")}
          onClick={props.onClose}
          size="small"
          disabled={props.isGeneratingSalaryNotices}
        />
        <PrimaryButton
          label={t("Salary.GenerateSalaryNoticesCreate")}
          onClick={() => void props.onCreate()}
          size="small"
          disabled={!props.canCreateSalaryNotices}
          isLoading={props.isGeneratingSalaryNotices}
        />
      </>
    );
  };

  const renderWorkerList = (): JSX.Element => (
    <>
      <GenerateSalaryNoticesWorkerCount>
        {props.generateSalaryNoticesMode === "all"
          ? t("Salary.GenerateSalaryNoticesAllWorkersCount", {
              count: props.salaryNoticeWorkers.length,
            })
          : t("Salary.GenerateSalaryNoticesSelectedWorkersCount", {
              count: props.selectedWorkerIds.length,
              total: props.salaryNoticeWorkers.length,
            })}
      </GenerateSalaryNoticesWorkerCount>
      <GenerateSalaryNoticesWorkerList>
        {props.salaryNoticeWorkers.map((worker) => {
          const isSelected = props.selectedWorkerIds.includes(worker.id);
          return (
            <ListRow
              key={worker.id}
              variant="dark"
              selected={isSelected}
              onClick={
                props.generateSalaryNoticesMode === "selected"
                  ? () => props.onToggleWorker(worker.id)
                  : undefined
              }
            >
              <AvatarChip
                title={getWorkerName(worker)}
                subTitle={`[ID: ${worker.id}]`}
                avatarUrl={
                  worker.externalId
                    ? props.avatarUrls[worker.externalId]
                    : undefined
                }
              />
              {props.generateSalaryNoticesMode === "selected" && (
                <GenerateSalaryNoticesWorkerAction
                  onClick={props.onWorkerActionClick}
                >
                  <Checkbox
                    checked={isSelected ? "checked" : "unchecked"}
                    onChange={() => props.onToggleWorker(worker.id)}
                  />
                </GenerateSalaryNoticesWorkerAction>
              )}
            </ListRow>
          );
        })}
      </GenerateSalaryNoticesWorkerList>
    </>
  );

  const renderWorkerState = (): JSX.Element => {
    switch (props.modalState) {
      case "loading":
        return (
          <Text typography="s1" color={ColorV2.Blue4}>
            {t("Salary.GenerateSalaryNoticesLoadingWorkers")}
          </Text>
        );
      case "error":
        return (
          <Text typography="s1" color={ColorV2.NegativeRed}>
            {t("Salary.GenerateSalaryNoticesWorkerLoadError")}
          </Text>
        );
      case "empty":
        return (
          <Text typography="s1" color={ColorV2.Blue4}>
            {t("Salary.GenerateSalaryNoticesNoActiveWorkers")}
          </Text>
        );
      case "list":
        return renderWorkerList();
      default:
        return assertNever(props.modalState);
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t(
        props.isCutOffWarningOpen
          ? "Salary.GenerateSalaryNoticesCutOffWarningTitle"
          : "Salary.GenerateSalaryNoticesTitle",
      )}
      size="small"
      showCloseButton={false}
      footer={renderFooter()}
    >
      {props.isCutOffWarningOpen ? (
        <GenerateSalaryNoticesModalContent>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesCutOffWarningDescription", {
              cutOffDate,
            })}
          </Text>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesCutOffWarningVisibility")}
          </Text>
        </GenerateSalaryNoticesModalContent>
      ) : (
        <GenerateSalaryNoticesModalContent>
          <Text typography="b2" color={ColorV2.Blue3}>
            {t("Salary.GenerateSalaryNoticesDescription")}
          </Text>
          <GenerateSalaryNoticesOptions role="radiogroup">
            <RadioButton
              name="generateSalaryNoticesMode"
              value="all"
              label={t("Salary.GenerateSalaryNoticesAllOption")}
              checked={props.generateSalaryNoticesMode === "all"}
              onChange={props.onModeChange}
            />
            <RadioButton
              name="generateSalaryNoticesMode"
              value="selected"
              label={t("Salary.GenerateSalaryNoticesSelectedOption")}
              checked={props.generateSalaryNoticesMode === "selected"}
              onChange={props.onModeChange}
            />
          </GenerateSalaryNoticesOptions>
          {renderWorkerState()}
        </GenerateSalaryNoticesModalContent>
      )}
    </Modal>
  );
};
